import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTenant } from '../contexts/TenantContext.tsx';
import { useCart } from '../contexts/CartContext.tsx';
import { SlotPicker } from '../components/SlotPicker.tsx';
import { supabase, toRpcError, ERROR_ACTIONS } from '../lib/supabase.ts';
import {
  formatMoney,
  formatPhoneInput,
  isValidPhone,
  FULFILLMENT_LABEL,
  PAYMENT_LABEL,
} from '../lib/format.ts';
import {
  toOrderPayload,
  buildIdempotencyKey,
  getCheckoutSalt,
  resetCheckoutSalt,
  saveOrderToken,
  saveLastOrder,
} from '../lib/cart.ts';
import type { FulfillmentMode, PaymentMethod, CreateOrderResult } from '../lib/types.ts';

export function CheckoutPage() {
  const navigate = useNavigate();
  const { snapshot, slug } = useTenant();
  const { lines, subtotal, clearCart } = useCart();
  const { tenant, location } = snapshot;

  const [fulfillment, setFulfillment] = useState<FulfillmentMode>(
    location.fulfillment_modes[0] ?? 'pickup',
  );
  const [slotStartAt, setSlotStartAt] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [payment, setPayment] = useState<PaymentMethod>(
    tenant.payments.on_pickup_cash ? 'cash_on_pickup' : 'card_on_pickup',
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const accentColor = tenant.theme.accent;
  const currency = tenant.currency;

  const needsDelivery = fulfillment === 'delivery';
  const phoneValid = isValidPhone(phone);

  const createOrder = useMutation({
    mutationFn: async () => {
      const salt = getCheckoutSalt(slug);
      const idempotencyKey = buildIdempotencyKey(
        slug,
        location.local_id,
        lines,
        slotStartAt,
        salt,
      );

      const { data, error } = await supabase.rpc('create_order', {
        p_slug: slug,
        p_location_local_id: location.local_id,
        p_fulfillment: fulfillment,
        p_slot_start_at: slotStartAt,
        p_items: toOrderPayload(lines),
        p_payment_method: payment,
        p_customer_name: name.trim() || null,
        p_customer_phone: phone.trim() || null,
        p_delivery_address: needsDelivery ? address.trim() : null,
        p_promo_code: promoCode.trim() || null,
        p_idempotency_key: idempotencyKey,
      });

      if (error) throw error;
      return data as CreateOrderResult;
    },
    onSuccess: (result) => {
      saveOrderToken(slug, {
        order_id: result.order_id,
        order_number: result.order_number,
        token: result.access_token ?? '',
        created_at: Date.now(),
      });
      saveLastOrder(slug, lines);
      resetCheckoutSalt(slug);
      clearCart();
      navigate(`/s/${slug}/order/${result.order_id}?token=${result.access_token ?? ''}`);
    },
    onError: (err) => {
      const rpcError = toRpcError(err);
      const action = rpcError.hint ? ERROR_ACTIONS[rpcError.hint] : null;
      setErrorMsg(action?.title ?? rpcError.message);
    },
  });

  function validate(): boolean {
    const next: Record<string, boolean> = { phone: true, address: needsDelivery };
    setTouched(next);

    if (needsDelivery && !phoneValid) return false;
    if (!phoneValid && phone.length > 0) return false;
    if (needsDelivery && !address.trim()) return false;
    return true;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setErrorMsg(null);
    createOrder.mutate();
  }

  if (lines.length === 0) {
    navigate(`/s/${slug}`);
    return null;
  }

  const availablePayments: Array<{ value: PaymentMethod; label: string }> = [
    ...(tenant.payments.on_pickup_cash ? [{ value: 'cash_on_pickup' as PaymentMethod, label: PAYMENT_LABEL.cash_on_pickup }] : []),
    ...(tenant.payments.on_pickup_card ? [{ value: 'card_on_pickup' as PaymentMethod, label: PAYMENT_LABEL.card_on_pickup }] : []),
    ...(tenant.payments.online.enabled ? [{ value: 'online' as PaymentMethod, label: PAYMENT_LABEL.online }] : []),
  ];

  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--bg)', paddingBottom: 100 }}
    >
      <PageHeader title="Оформление" onBack={() => navigate(`/s/${slug}/cart`)} />

      <form onSubmit={handleSubmit} noValidate>
        <main className="flex flex-col gap-4 px-4 pt-4">

          {/* Fulfillment mode */}
          {location.fulfillment_modes.length > 1 && (
            <Section title="Способ получения">
              <div className="flex gap-2" role="group" aria-label="Способ получения">
                {location.fulfillment_modes.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setFulfillment(mode)}
                    aria-pressed={fulfillment === mode}
                    className="flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors"
                    style={{
                      background: fulfillment === mode ? accentColor : 'var(--surface)',
                      color: fulfillment === mode ? '#fff' : 'var(--text-muted)',
                      border: `1px solid ${fulfillment === mode ? accentColor : 'var(--border)'}`,
                    }}
                  >
                    {FULFILLMENT_LABEL[mode]}
                  </button>
                ))}
              </div>
            </Section>
          )}

          {/* Slot picker */}
          <Section title="Время">
            <SlotPicker
              locationId={location.id}
              timezone={location.timezone}
              accentColor={accentColor}
              value={slotStartAt}
              onChange={setSlotStartAt}
            />
          </Section>

          {/* Contacts */}
          <Section title="Контакты">
            <div className="flex flex-col gap-3">
              <Field
                id="checkout-name"
                label="Имя (необязательно)"
                value={name}
                onChange={(v) => setName(v)}
                placeholder="Как к вам обращаться"
                autoComplete="given-name"
              />
              <Field
                id="checkout-phone"
                label={needsDelivery ? 'Телефон *' : 'Телефон'}
                value={phone}
                onChange={(v) => setPhone(formatPhoneInput(v))}
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                placeholder="+7 999 123-45-67"
                inputMode="tel"
                autoComplete="tel"
                error={
                  touched.phone && phone.length > 0 && !phoneValid
                    ? 'Введите корректный номер телефона'
                    : undefined
                }
              />
            </div>
          </Section>

          {/* Delivery address */}
          {needsDelivery && (
            <Section title="Адрес доставки">
              <Field
                id="checkout-address"
                label="Адрес *"
                value={address}
                onChange={(v) => setAddress(v)}
                onBlur={() => setTouched((t) => ({ ...t, address: true }))}
                placeholder="Улица, дом, квартира"
                autoComplete="street-address"
                error={
                  touched.address && !address.trim() ? 'Укажите адрес доставки' : undefined
                }
              />
            </Section>
          )}

          {/* Promo */}
          <Section title="Промокод">
            <Field
              id="checkout-promo"
              label="Код"
              value={promoCode}
              onChange={(v) => setPromoCode(v.toUpperCase())}
              placeholder="SHAWARMA10"
              autoComplete="off"
            />
          </Section>

          {/* Payment */}
          {availablePayments.length > 1 && (
            <Section title="Оплата">
              <div className="flex flex-col gap-2" role="group" aria-label="Способ оплаты">
                {availablePayments.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-3 rounded-xl px-4 py-3"
                    style={{
                      background: payment === value ? 'var(--accent-soft)' : 'var(--surface)',
                      border: `1px solid ${payment === value ? accentColor : 'var(--border)'}`,
                      minHeight: 48,
                    }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={value}
                      checked={payment === value}
                      onChange={() => setPayment(value)}
                      className="h-4 w-4"
                      style={{ accentColor }}
                    />
                    <span className="text-sm" style={{ color: 'var(--text)' }}>{label}</span>
                  </label>
                ))}
              </div>
            </Section>
          )}

          {/* Order summary */}
          <Section title="Ваш заказ">
            <ul className="flex flex-col gap-1.5">
              {lines.map((line) => (
                <li key={line.line_id} className="flex justify-between gap-3 text-sm">
                  <span style={{ color: 'var(--text)' }}>
                    {line.item_name} × {line.quantity}
                  </span>
                  <span className="flex-shrink-0 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                    {formatMoney(line.estimated_unit_price * line.quantity, currency)}
                  </span>
                </li>
              ))}
            </ul>
            <div
              className="mt-3 flex items-center justify-between border-t pt-3"
              style={{ borderColor: 'var(--border)' }}
            >
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Итого (оценка)
              </span>
              <span className="text-base font-bold tabular-nums" style={{ color: 'var(--text)' }}>
                {formatMoney(subtotal, currency)}
              </span>
            </div>
            <p className="mt-1.5 text-xs" style={{ color: 'var(--text-subtle)' }}>
              Точная сумма рассчитывается на сервере с учётом акций и доставки
            </p>
          </Section>

          {/* Error */}
          {errorMsg && (
            <div
              role="alert"
              className="rounded-xl p-3 text-sm font-medium"
              style={{ background: 'oklch(55% 0.200 25 / 0.1)', color: 'var(--danger)' }}
            >
              {errorMsg}
            </div>
          )}
        </main>

        {/* Submit */}
        <div
          className="fixed bottom-0 left-0 right-0 border-t p-3"
          style={{
            background: 'var(--surface)',
            borderColor: 'var(--border)',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          }}
        >
          <button
            type="submit"
            disabled={createOrder.isPending}
            className="flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold disabled:opacity-60"
            style={{ background: accentColor, color: '#fff' }}
          >
            {createOrder.isPending ? 'Отправляем…' : 'Подтвердить заказ'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="rounded-2xl p-4"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}
    >
      <h2 className="mb-3 text-sm font-semibold" style={{ color: 'var(--text)' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  error?: string;
}

function Field({ id, label, value, onChange, onBlur, placeholder, inputMode, autoComplete, error }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
        className="h-11 rounded-lg px-3 text-sm"
        style={{
          background: 'var(--n-50)',
          border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
          color: 'var(--text)',
        }}
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
    </div>
  );
}

function PageHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 px-2 py-3"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
    >
      <button
        onClick={onBack}
        aria-label="Назад"
        className="hit-target rounded-lg"
        style={{ color: 'var(--text)' }}
      >
        ←
      </button>
      <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
        {title}
      </h1>
    </header>
  );
}
